import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { get, groupBy } from 'lodash';
import { StepType } from '../components/job-control/step.type';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { ICustomTaskAction } from '../interfaces/custom-task-action';
import {
  IOrganisationSpecificStepGroup,
  IOrganisationSpecificSteps,
  IOrganisationSpecificStepsGroupSearchResult,
} from '../interfaces/organisation-specific-steps';
import { ITaskOrganisationSpecificSteps, ITaskStep, ITaskStepNode } from '../interfaces/task-info';
import { IFreeTextType, IQuestionType } from '../interfaces/question-type';
import {
  IChecklistTaskTypeStep,
  IGenericTaskStep,
  IChecklistTaskTypeStepDetail,
} from '../interfaces/checklist-task-type';
import { EditService } from './edit.service';
import { Operation } from 'fast-json-patch';
import { LoggerService } from './logger.service';
import { TaggedLogger } from './taggedLogger';
import { AncillaryStepDTO } from '../interfaces/enhanced-checklist/ancillary-step-dto.interface';

interface NormalizedSteps {
  [key: string]: any;
  isPickup: boolean;
  stepTypeId: number;
}

@Injectable({ providedIn: 'root' })
export class OrganisationSpecificStepsService extends EditService<any, null> {
  public readonly logger: TaggedLogger;
  /**
   * The default pickup step type
   * this is the step that pickup org steps/custom steps should come after if they don't have a specific sort order
   */
  public readonly defaultPickupStepType = StepType.ArrivedToPickup;
  /**
   * The default Dropoff step type
   * this is the step that dropoff org steps/custom steps should come after if they don't have a specific sort order
   */
  public readonly defaultDropoffStepType = StepType.EndMovement;

  public url = '/api/org';
  entityTitle = '';

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    loggerService: LoggerService,
  ) {
    super(http, handlersFactory);
    this.logger = loggerService.taggedLogger('service:organisation-specific-steps');
  }

  saveSpecificSteps(data: any): Promise<any> {
    console.log(data);
    return this.http.post('api/Organisations/savespecificstep', data).toPromise().catch();
  }

  patchSpecificStepsGroup(id: number, patch: Operation[]) {
    return this.http.patch(`api/OrganisationSpecificSteps/Group/${id}`, patch).toPromise().catch();
  }

  updateSpecificStepsTaskStepDetail(
    taskTypeId: number,
    checklistId: number,
    stepDetails: IGenericTaskStep[],
  ): Promise<any> {
    // todo: expecting issue here: When saving one task type, sometimes the others are messed up.
    this.logger.debug('updateSpecificStepsTaskStepDetail()', stepDetails);
    return this.handleRequest(
      this.http.put(
        `api/org/TaskStepDetail/updateTaskStepDetail/${taskTypeId}/${checklistId}`,
        stepDetails,
      ),
      this.handlersFactory.getDefault(),
    ).catch(this.handleError);
  }

  getSpecificSteps(): Promise<any> {
    return this.http.get('api/Organisations/loadspecificsteps').toPromise().catch();
  }

  getSpecificStepsGroups(params?): Promise<IOrganisationSpecificStepsGroupSearchResult> {
    return this.http
      .get<IOrganisationSpecificStepsGroupSearchResult>('api/OrganisationSpecificSteps/Groups', {
        params: params,
      })
      .toPromise()
      .catch();
  }

  getSpecificStepsForCurrentUser(): Promise<Array<IOrganisationSpecificSteps>> {
    return this.http
      .get<IOrganisationSpecificSteps[]>('api/org/loadspecificstepsforcurrentuser')
      .toPromise()
      .catch();
  }

  getChecklistSteps(checklistId: number): Promise<IOrganisationSpecificSteps[]> {
    return this.http
      .get<IOrganisationSpecificSteps[]>(`api/org/checklist-steps/${checklistId}`)
      .toPromise()
      .catch();
  }

  getChecklistTaskTypeStepDetail(
    checklistId: number,
    taskTypeId: number,
    activeOnly: boolean,
  ): Promise<IChecklistTaskTypeStep> {
    return this.http
      .get<IChecklistTaskTypeStep>(
        `api/org/checklistTaskTypeSteps/${checklistId}/taskType/${taskTypeId}?activeOnly=${activeOnly}`,
      )
      .toPromise()
      .catch();
  }

  /**
   * Posts a request to create a checklist mapping
   * @param checklistMapping The checklist mapping
   * @param autoApply If true, applies to all other task types
   * @throws exception if HTTP error occurs Expect it to be a HTTP 400
   * @return Resolves HTTP 200 with no body
   */
  createChecklistTaskTypeMapping(
    checklistMapping: IChecklistTaskTypeStep,
    autoApply: boolean,
  ): Promise<void> {
    this.logger.debug(`updateChecklistTaskTypeMapping(${autoApply})`, checklistMapping);
    return this.http
      .post<any>(`api/org/checklistTaskType/${autoApply}`, checklistMapping)
      .toPromise();
  }

  /**
   * Puts a request to update a checklist mapping
   * @param checklistMapping The checklist mapping
   * @param autoApply If true, applies to all other task types
   * @throws exception if HTTP error occurs Expect it to be a HTTP 400
   * @return Resolves HTTP 200 with no body if successful
   */
  updateChecklistTaskTypeMapping(
    checklistMapping: IChecklistTaskTypeStep,
    autoApply: boolean,
  ): Promise<void> {
    this.logger.debug(`updateChecklistTaskTypeMapping(${autoApply})`, checklistMapping);
    return this.http
      .put<any>(`api/org/checklistTaskType/${autoApply}`, checklistMapping)
      .toPromise();
  }

  /** Get list of checklist task type mappings for the current user's organisation */
  public getMappedChecklists(
    departmentId: number,
    taskTypeId?: number,
    activeOnly?: boolean,
  ): Promise<IChecklistTaskTypeStep[]> {
    let params = new HttpParams();

    params = params.set('departmentId', String(departmentId));

    if (taskTypeId != null) {
      params = params.set('taskTypeId', String(taskTypeId));
    }

    if (activeOnly != null) {
      params = params.set('activeOnly', String(activeOnly));
    }

    console.log('PARAMS', params.toString());

    return this.http.get<any>(`${this.url}/checklist/mapped`, { params }).toPromise();
  }

  public getQuestionTypes(): Promise<IQuestionType[]> {
    return this.http.get<any>(`${this.url}/question-types`).toPromise();
  }

  public getFreeTextTypes(): Promise<IFreeTextType[]> {
    return this.http.get<any>(`${this.url}/free-text-types`).toPromise();
  }

  createChecklistClone(
    selectedChecklist: IOrganisationSpecificStepGroup,
    customerId: number,
    locked: boolean = true,
    groupName: string = '',
  ): Promise<any> {
    let queryParams = '?locked=' + locked + '&groupName=' + encodeURIComponent(groupName);

    return this.handleRequest(
      this.http.get<any>(
        `${this.url}/createChecklistClone/${selectedChecklist.id}/customer/${customerId}${queryParams}`,
      ),
      this.handlersFactory.getForReadAll(),
    ).then((result) => result);
  }

  unlockChecklist(selectedChecklist): Promise<any> {
    return this.handleRequest(
      this.http.get<any>(
        `${this.url}/unlockChecklist/${selectedChecklist.id}/customer/${selectedChecklist.organisationId}`,
      ),
      this.handlersFactory.getForReadAll(),
    ).then((result) => result);
  }

  removeChecklistClone(checklistGroupId: number, customerId: number): Promise<any> {
    return this.handleRequest(
      this.http.delete<any>(
        `${this.url}/deleteChecklistClone/${checklistGroupId}/customer/${customerId}`,
      ),
      this.handlersFactory.getForReadAll(),
    ).then((result) => result);
  }

  public deleteAnswerTemplate(orgStepId: number, answerId: number): Promise<boolean> {
    return this.http.delete<any>(`${this.url}/answer/${orgStepId}/${answerId}`).toPromise();
  }

  public getAncillarySteps(): Promise<AncillaryStepDTO[]> {
    return this.handleRequest(
      this.http.get<any>(`${this.url}/ancillary-steps`),
      this.handlersFactory.getForReadAll(),
    ).then((result) => result);
  }

  /**
   * Given a Task Type and the Checklist, this merges the steps together in the required order.
   * @param selectedTaskTypeSteps Engineius Steps for a particular task type
   * @param checklistTaskTypeSteps The checklist steps entry for a particular task type.
   * @return the merged list of steps
   */
  public buildTaskStepsChecklistStepList(
    selectedTaskTypeSteps: ITaskStep[],
    checklistTaskTypeSteps: IChecklistTaskTypeStep,
    defaultChecklistSteps: IOrganisationSpecificSteps[],
  ): IGenericTaskStep[] {
    this.logger.debug(
      'buildTaskStepsChecklistStepList():',
      selectedTaskTypeSteps,
      checklistTaskTypeSteps,
      defaultChecklistSteps,
    );

    const engineiusSteps = this.mapTaskTypeStepsToGeneric(selectedTaskTypeSteps);

    let mappedSteps: IGenericTaskStep[];

    // If no mapping exists, use the default steps
    if (
      !get(checklistTaskTypeSteps, 'stepsTaskTypeDetails.length') ||
      checklistTaskTypeSteps.stepsTaskTypeDetails.length != defaultChecklistSteps.length
    ) {
      this.logger.debug('No mapping');
      mappedSteps = this.mapCheckListStepsToGeneric(
        defaultChecklistSteps,
        checklistTaskTypeSteps?.stepsTaskTypeDetails,
      );
    } else {
      this.logger.debug('Mapping Exists');
      mappedSteps = this.mapCheckListStepsTaskTypeToGeneric(
        checklistTaskTypeSteps.stepsTaskTypeDetails,
      );
    }

    return this.sortStepsWithEnhancedChecklistSteps(engineiusSteps, mappedSteps);
  }

  /**
   * Adds Engineius Steps to Organisation Specific steps and sorts the final list.
   * @param engineiusSteps The engineius steps
   * @param checklistSteps The checklist steps
   * @return The spliced list of the two, sorted correctly.
   */
  public sortStepsWithEnhancedChecklistSteps(
    engineiusSteps: IGenericTaskStep[] | ITaskStepNode[],
    checklistSteps: IGenericTaskStep[] | ITaskOrganisationSpecificSteps[] | ICustomTaskAction[],
  ): IGenericTaskStep[] {
    const result = this.normalizeSteps([...engineiusSteps]);
    const organisationSpecificSteps = this.normalizeSteps([...checklistSteps]);
    const grouped = groupBy(organisationSpecificSteps, 'parentEngineiusStepId');

    for (const [parentId, children] of Object.entries(grouped)) {
      const mappedChildren = [...children];
      const sortedChildren = mappedChildren.sort((a, b) => a.order - b.order);
      const parentStepIndex = result.findIndex((step) => `${step.stepTypeId}` === `${parentId}`);
      // If we have a parent step index, that's where these are going
      if (parentStepIndex !== -1) {
        result.splice(parentStepIndex + 1, 0, ...sortedChildren);
      } else {
        // Otherwise, they're going one by one into either the default spot for pickup or dropoff
        const groupedByIsPickup = groupBy(sortedChildren, 'isPickup');
        for (const [isPickup, leftoverChildren] of Object.entries(groupedByIsPickup)) {
          let spliceIndex = -1;
          const sortedLeftoverChildren = leftoverChildren.sort((a, b) => a.order - b.order);

          if (isPickup === 'true') {
            spliceIndex = result.findIndex(
              (step) => step.stepTypeId === this.defaultPickupStepType,
            ); // arrived
          } else {
            spliceIndex = result.findIndex(
              (step) => step.stepTypeId === this.defaultDropoffStepType,
            ); // end movemen
          }

          result.splice(spliceIndex + 1, 0, ...sortedLeftoverChildren);
        }
      }
    }

    return result as any;
  }

  /**
   * Used in the ehnahnced checklist sort method above.
   * Normalizes the steps so that we can always use:
   * - isPickup to mean it's a pickup step
   * - stepTypeId to get the step type id
   *
   * Used because there's multiple interfaces sorted by this method and none of them have the same property names
   * @param steps The steps to normalize
   * @return the normalized steps
   * @private
   */
  private normalizeSteps(steps: any[]): NormalizedSteps[] {
    return steps.map((step) => ({
      ...step,
      isPickup: step.isPickup || step.isPickUp,
      stepTypeId: step.stepTypeId != undefined ? step.stepTypeId : step.stepType,
    }));
  }

  public mapTaskTypeStepsToGeneric(checkListSteps: ITaskStep[]): IGenericTaskStep[] {
    let mapped = checkListSteps.map((val, index) => {
      return <IGenericTaskStep>{
        id: val.id,
        name: val.name,
        order: val.order,
        isActive: true,
        isEngineiusStep: true,
        isPickUp: val.isPickUp,
        stepTypeId: val.stepType, // only relevant with Engineius steps
        parentEngineiusStepId: null,
        organisationSpecificStepsTaskTypeId: 0,
        organisationSpecificStepId: 0,
        isTaskTypeDetail: false,
        organisationSpecificStep: null,
      };
    });
    return mapped.sort((a, b) => a.order - b.order);
  }

  public mapCheckListStepsToGeneric(
    checkListSteps: IOrganisationSpecificSteps[],
    checklistStepDetail: IChecklistTaskTypeStepDetail[],
  ): IGenericTaskStep[] {
    let mapped = checkListSteps.map((val, index) => {
      const stepMatch = checklistStepDetail.find(
        (step) => step.organisationSpecificStepId === val.id,
      );

      return <IGenericTaskStep>{
        id: stepMatch ? stepMatch.id : val.id,
        name: val.name,
        order: val.order,
        isEngineiusStep: false,
        isPickUp: val.isPickUp,
        stepTypeId: null,
        parentEngineiusStepId: null,
        organisationSpecificStepsTaskTypeId: 0,
        isActive: true,
        organisationSpecificStepId: val.id,
        isTaskTypeDetail: false,
        organisationSpecificStep: val,
      };
    });
    return mapped.sort((a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0));
  }

  public mapCheckListStepsTaskTypeToGeneric(
    checkListSteps: IChecklistTaskTypeStepDetail[],
  ): IGenericTaskStep[] {
    let mapped = checkListSteps.map((val, index) => {
      return <IGenericTaskStep>{
        id: val.id,
        name: val.name,
        order: val.order,
        isEngineiusStep: false,
        isActive: val.isActive,
        isPickUp: val.isPickUp,
        stepTypeId: null,
        parentEngineiusStepId: val.parentEngineiusStepId,
        organisationSpecificStepsTaskTypeId: val.organisationSpecificStepsTaskTypeId,
        organisationSpecificStepId: val.organisationSpecificStepId,
        isTaskTypeDetail: true,
        organisationSpecificStep: val.organisationSpecificStep,
      };
    });
    return mapped.sort((a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0));
  }
}
